import React, { useEffect, useRef, useState } from "react";
import { WD, pad2, ymdParts, buildMonthCells } from "./dateUtil";
import useEscapeKey from "./useEscapeKey";

// 日付ピッカー（案B）。日付欄をタップすると月カレンダーが開く。
//  ・PC : 欄の直下にポップオーバー
//  ・スマホ: 下から出るシート（860px以下でCSSが切替）
//  ・isDisabled(dateStr) が true の日 → 薄く表示・クリック不可（シフト休/祝日/過去日など）。
//  ・isHoliday(dateStr) が true の日 → 日付を赤字（祝日）。
//  ・minYm より前の月へは戻れない（過去月は選べる日が無いため）。
// カレンダーのマス目は既存の .mp-cal-grid / .mp-cal-cell / .mp-cal-day を再利用する。
function fmtField(ds) {
  const d = new Date(`${ds}T00:00:00`);
  if (Number.isNaN(d.getTime())) return ds;
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日(${WD[d.getDay()]})`;
}

const IconCal = () => (
  <svg className="mp-dpk-cal" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" />
  </svg>
);
const IconChevron = () => (
  <svg className="mp-dpk-chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
);

// initialYm（"YYYY-MM"）＝未選択時にカレンダーを最初に開く月。省略時は minYm（従来動作）。
// dayMarker(dateStr)（任意）＝ その日に小さな印を出す。"record"=打刻あり(緑ドット)。省略時は無印。
function DatePickerCalendar({ value, onChange, isDisabled, isHoliday, holidayName, minYm, initialYm, placeholder, dayMarker }) {
  const [open, setOpen] = useState(false);
  const startYm = value || initialYm || minYm;
  const initY = parseInt(startYm.slice(0, 4), 10);
  const initM = parseInt(startYm.slice(5, 7), 10);
  const [view, setView] = useState({ y: initY, m: initM });
  // スマホ用：祝日名チップを開いている日（PCはホバーで出すので不要）。
  const [tipDate, setTipDate] = useState(null);
  const rootRef = useRef(null);

  // 開閉・月移動で祝日チップを閉じる。
  useEffect(() => { setTipDate(null); }, [open, view.y, view.m]);

  // 開くたびに選択中の月へ合わせる。
  useEffect(() => {
    if (open && value) setView({ y: parseInt(value.slice(0, 4), 10), m: parseInt(value.slice(5, 7), 10) });
  }, [open, value]);

  // Esc で閉じる。
  useEscapeKey(() => setOpen(false), open);

  // PC：外側クリックで閉じる（欄・カレンダー内のクリックは root 内なので閉じない）。
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const viewYm = `${view.y}-${pad2(view.m)}`;
  const atMin = viewYm <= minYm;
  const prev = () => setView((v) => (v.m === 1 ? { y: v.y - 1, m: 12 } : { y: v.y, m: v.m - 1 }));
  const next = () => setView((v) => (v.m === 12 ? { y: v.y + 1, m: 1 } : { y: v.y, m: v.m + 1 }));

  const cells = buildMonthCells(view.y, view.m);

  const pick = (ds) => { onChange(ds); setOpen(false); };

  return (
    <div className="mp-dpk" ref={rootRef}>
      <button
        type="button"
        className={`mp-dpk-field${value ? " mp-on" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <IconCal />
        <span className="mp-dpk-fieldval">{value ? fmtField(value) : (placeholder || "日付を選ぶ")}</span>
        <IconChevron />
      </button>

      {open && (
        <>
          <div className="mp-dpk-backdrop" onClick={() => setOpen(false)} />
          <div className="mp-dpk-pop" role="dialog" aria-label="日付を選択">
            <div className="mp-dpk-handle" />
            <div className="mp-dpk-head">
              <button type="button" className="mp-btn mp-ghost mp-sm" aria-label="前の月" disabled={atMin} onClick={prev}>◀</button>
              <div className="mp-dpk-title">{view.y}年{view.m}月</div>
              <button type="button" className="mp-btn mp-ghost mp-sm" aria-label="次の月" onClick={next}>▶</button>
            </div>
            <div className="mp-cal-grid mp-cal-head">
              {WD.map((w, i) => (
                <div key={w} className={`mp-cal-wd${i === 0 ? " mp-cal-sun" : ""}${i === 6 ? " mp-cal-sat" : ""}`}>{w}</div>
              ))}
            </div>
            <div className="mp-cal-grid">
              {cells.map((d, i) => {
                if (d == null) return <div key={i} className="mp-cal-cell mp-cal-empty" />;
                const ds = ymdParts(view.y, view.m, d);
                const disabled = isDisabled(ds);
                const selected = ds === value;
                const hol = isHoliday && isHoliday(ds);
                const holName = hol && holidayName ? holidayName(ds) : null;
                // 選択可＝onClickで選択。祝日（選択不可）は名前チップの開閉のみ（PCはホバーでも出る）。
                const clickHoliday = disabled && !!holName;
                const onCellClick = !disabled
                  ? () => pick(ds)
                  : clickHoliday
                    ? () => setTipDate((p) => (p === ds ? null : ds))
                    : undefined;
                // 打刻あり(record)の日は緑ドットで示す（訂正できる日の目印）。選択不可日には出さない。
                const marker = !disabled && dayMarker ? dayMarker(ds) : null;
                const cls = `mp-cal-cell${disabled ? " mp-dpk-disabled" : " mp-cal-click"}`
                  + `${selected ? " mp-dpk-sel" : ""}${holName ? " mp-dpk-holcell" : ""}${tipDate === ds ? " mp-tip-on" : ""}`;
                return (
                  <div
                    key={i}
                    className={cls}
                    role={onCellClick ? "button" : undefined}
                    tabIndex={onCellClick ? 0 : undefined}
                    aria-disabled={disabled ? true : undefined}
                    title={holName || undefined}
                    onClick={onCellClick}
                    onKeyDown={onCellClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onCellClick(); } } : undefined}
                  >
                    <span className={`mp-cal-day${hol ? " mp-dpk-hol" : ""}`}>{d}</span>
                    {marker === "record" ? <span className="mp-dpk-recdot" aria-hidden="true" /> : null}
                    {holName ? <span className="mp-dpk-tip" role="tooltip">{holName}</span> : null}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DatePickerCalendar;
